import { ENGINE_PROVIDER } from "@/lib/engine";
import { useEffect, useMemo, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { X } from "lucide-react";
import type { Backtest, BacktestOutputs, ParamSpec } from "@/api/types";
import { PlotValuesTable } from "@/components/PlotValuesTable";
import { buildConfigPayload, fillParamDefaults, DynamicForm } from "@/components/DynamicForm";
import { useApi } from "@/context/ApiContext";
import {
  presentTradingViewEngineExtras,
  presentTradingViewEngineProperties,
  stripUiOnlyEngineFields,
  tradingViewEnginePropertyDefaults,
  UI_ONLY_ENGINE_FIELDS,
} from "@/lib/engine-properties";
import { normalizeConfigurableParams } from "@/lib/configurable-params";
import { buildFormStrategyConfig, buildEngineSaveConfig, sameParamValue } from "@/lib/strategy-leverage";
import { barMagnifierOptions, effectiveBarMagnifier } from "@/lib/bar-magnifier";
import { declaredRecalculate, recalculateLabel } from "@/lib/recalculate";
import { fetchStrategyInfo, suggestedEngineConfig, type StrategyInfoResponse } from "@/lib/strategy-info";
import { formatScriptRef, resolveStrategySource } from "@/lib/script-ref";
import { ensurePinnedStrategyConfig, scriptIdFromMetadata } from "@/lib/script-save";

/** Tab order: 0 = Properties, 1 = Inputs, 2 = Advanced, 3 = Pine output. */
const TABS = ["Properties", "Inputs", "Advanced", "Pine output"] as const;

function patchEngineFromForm(
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
  specs: ParamSpec[]
): Record<string, unknown> {
  const cleaned = stripUiOnlyEngineFields(next);
  const patch: Record<string, unknown> = {};
  for (const s of specs) {
    if (s.disabled || UI_ONLY_ENGINE_FIELDS.has(s.name)) continue;
    if (cleaned[s.name] === undefined) continue;
    patch[s.name] = cleaned[s.name];
  }
  return { ...prev, ...patch };
}

function applyEnginePatch(
  setter: Dispatch<SetStateAction<Record<string, unknown>>>,
  ref: MutableRefObject<Record<string, unknown>>,
  next: Record<string, unknown>,
  specs: ParamSpec[]
) {
  setter((prev) => {
    const merged = patchEngineFromForm(prev, next, specs);
    ref.current = merged;
    return merged;
  });
}

type Props = {
  open: boolean;
  backtest: Backtest;
  onClose: () => void;
  /** Called after the properties are saved; the page re-runs with them. */
  onSaved: (updated: Backtest) => void;
  /** Latest plot/alert snapshot from the last run. Read-only. */
  outputs?: BacktestOutputs | null;
  outputsWaiting?: boolean;
};

export function BacktestSettingsModal({
  open,
  backtest,
  onClose,
  onSaved,
  outputs,
  outputsWaiting = false,
}: Props) {
  const api = useApi(["scripts"]);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Stable refs so ApiContext re-renders (new `api` object each time) don't re-trigger load.
  const requestRef = useRef(api.commands);
  const scriptsRef = useRef(api.scripts);
  requestRef.current = api.commands;
  scriptsRef.current = api.scripts;

  const [tab, setTab] = useState(0);
  const [name, setName] = useState(backtest.name || "");
  const [strategyConfig, setStrategyConfig] = useState<Record<string, unknown>>(
    () => ({ ...(backtest.strategy_config || {}) })
  );
  const [engineConfig, setEngineConfig] = useState<Record<string, unknown>>(
    () => ({ ...(backtest.engine_config || {}) })
  );
  // Always-current snapshot so Save can't race a stale React state render.
  const engineConfigRef = useRef(engineConfig);
  engineConfigRef.current = engineConfig;
  const [strategySpecs, setStrategySpecs] = useState<ParamSpec[]>([]);
  const [engineSpecs, setEngineSpecs] = useState<ParamSpec[]>([]);
  /** The server's reading of the script's strategy(): what TradingView would run it with. */
  const [strategyInfo, setStrategyInfo] = useState<StrategyInfoResponse | null>(null);
  /** Bar Magnifier timeframe ("" = off) and the exchange's timeframes it is picked from. */
  const [barMagnifier, setBarMagnifier] = useState("");
  const [marketTimeframes, setMarketTimeframes] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const engineProvider = ENGINE_PROVIDER;

  // Snapshot identity when opening — avoid re-loading on parent object-ref churn.
  const openKey = open ? `${backtest.id}:${engineProvider}` : null;

  useEffect(() => {
    if (!openKey) {
      setReady(false);
      return;
    }

    setTab(0);
    setName(backtest.name || "");
    setStrategyConfig({ ...(backtest.strategy_config || {}) });
    const loadedEngine = { ...(backtest.engine_config || {}) };
    engineConfigRef.current = loadedEngine;
    setEngineConfig(loadedEngine);
    setBarMagnifier(effectiveBarMagnifier(backtest.timeframe, backtest.bar_magnifier));
    setError(null);
    setReady(false);

    const sourceHint = String(
      ensurePinnedStrategyConfig(backtest.strategy_config, {
        metadata: backtest.metadata,
        scripts: scriptsRef.current || [],
        strategyProvider: backtest.strategy_provider || "pinescript",
      }).source || ""
    );
    let cancelled = false;
    async function load() {
      try {
        let source = await resolveStrategySource(
          requestRef.current,
          sourceHint,
          scriptsRef.current || []
        );
        // Last resort: metadata script_id tip when pin/resolve still yielded nothing.
        if (!source.trim()) {
          const sid = scriptIdFromMetadata(backtest.metadata);
          const matched = sid
            ? (scriptsRef.current || []).find((s: { id: string; source?: string; rev_no?: number }) => s.id === sid)
            : undefined;
          if (matched?.source) {
            source = matched.source;
          } else if (sid) {
            source = await resolveStrategySource(
              requestRef.current,
              formatScriptRef(sid, matched?.rev_no ?? 1),
              scriptsRef.current || []
            );
          }
        }
        const cfg: Record<string, string> = {};
        if (source.trim()) cfg.source = source;

        const [strategyRaw, engineRaw, info, tfs] = await Promise.all([
          requestRef.current.getParams({
            category: "strategy",
            name: "pinescript",
            config: cfg,
          }),
          requestRef.current.getParams({
            category: "engine",
            name: engineProvider,
            config: {},
          }),
          fetchStrategyInfo(requestRef.current, sourceHint || source),
          requestRef
            .current.getMarketTimeframes({ market_provider: backtest.market_provider, market_config: backtest.market_config || {} })
            .catch(() => null),
        ]);
        if (cancelled) return;
        setStrategyInfo(info);
        setMarketTimeframes(Array.isArray(tfs) ? (tfs as string[]) : []);
        const specs = normalizeConfigurableParams(strategyRaw).specs;
        setStrategySpecs(specs);
        setEngineSpecs(normalizeConfigurableParams(engineRaw).specs);
        setStrategyConfig((prev) => ({
          ...prev,
          ...(sourceHint ? { source: sourceHint } : source ? { source } : {}),
        }));
        if (!source.trim()) {
          setError(
            "Script source is missing — open the Script panel, Save, then reopen Settings to load inputs."
          );
        }
        setReady(true);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load settings");
          setReady(true);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // Only reload when the dialog opens or backtest identity changes — not on ApiContext churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey, engineProvider]);

  // The latest handler, read at key time: callers pass a new function on every
  // render, and re-running the effect for it would pull focus out of the fields.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const root = dialogRef.current;
    const focusables = root?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusables?.[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [open]);

  const inputSpecs = useMemo(
    () => strategySpecs.filter((s) => s.name !== "source" && s.type !== "script"),
    [strategySpecs]
  );
  const enginePropertySpecs = useMemo(
    () => presentTradingViewEngineProperties(engineSpecs),
    [engineSpecs]
  );
  const engineExtrasSpecs = useMemo(
    () => presentTradingViewEngineExtras(engineSpecs),
    [engineSpecs]
  );
  const strategyValues = useMemo(
    () => fillParamDefaults(strategyConfig, strategySpecs),
    [strategyConfig, strategySpecs]
  );
  const enginePropertyValues = useMemo(
    () => ({
      ...tradingViewEnginePropertyDefaults(enginePropertySpecs),
      ...fillParamDefaults(engineConfig, engineSpecs),
    }),
    [enginePropertySpecs, engineConfig, engineSpecs]
  );
  const magnifierOptions = useMemo(() => barMagnifierOptions(backtest.timeframe, marketTimeframes), [backtest.timeframe, marketTimeframes]);
  const declaresMagnifier = strategyInfo?.info?.backtest?.use_bar_magnifier === true;
  const scriptEngine = useMemo(() => suggestedEngineConfig(strategyInfo, engineSpecs, engineProvider), [strategyInfo, engineSpecs, engineProvider]);
  const matchesScript = useMemo(
    () => !scriptEngine || Object.entries(scriptEngine).every(([k, v]) => sameParamValue(enginePropertyValues[k], v)),
    [scriptEngine, enginePropertyValues]
  );
  const engineExtrasValues = useMemo(
    () => ({
      ...tradingViewEnginePropertyDefaults(engineExtrasSpecs),
      ...fillParamDefaults(engineConfig, engineSpecs),
    }),
    [engineExtrasSpecs, engineConfig, engineSpecs]
  );

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      if (!name.trim()) throw new Error("Name is required");
      const strategyValues = fillParamDefaults(strategyConfig, strategySpecs);
      const metaScriptId =
        typeof backtest.metadata?.ui?.script_id === "string" ? backtest.metadata.ui.script_id : "";
      const matched = metaScriptId
        ? api.scripts.find((s: { id: string; rev_no?: number }) => s.id === metaScriptId)
        : undefined;
      const pinned =
        String(backtest.strategy_config?.source || "") ||
        String(strategyValues.source || "") ||
        (matched ? formatScriptRef(matched.id, matched.rev_no ?? 1) : "");
      const strategy_config = buildFormStrategyConfig(
        {
          ...strategyValues,
          source: pinned,
        },
        strategySpecs
      );
      if (!String(strategy_config.source || "").trim()) {
        throw new Error("Strategy script source is missing — re-open the Script panel and Save.");
      }

      const engine_config = buildEngineSaveConfig(
        engineConfigRef.current,
        engineSpecs,
        engineProvider,
        buildConfigPayload,
        fillParamDefaults
      );

      const updated = (await api.commands.updateBacktest({
        id: backtest.id,
        name: name.trim(),
        strategy_provider: backtest.strategy_provider || "pinescript",
        strategy_config,
        engine_provider: engineProvider,
        engine_config,
        // "" turns it off; a value the chart timeframe no longer fits is dropped.
        bar_magnifier: effectiveBarMagnifier(backtest.timeframe, barMagnifier),
        enabled: false,
      })) as Backtest;

      const savedInitial = Number(updated?.engine_config?.initial);
      const sentInitial = Number(engine_config.initial);
      if (
        Number.isFinite(sentInitial) &&
        Number.isFinite(savedInitial) &&
        savedInitial !== sentInitial
      ) {
        throw new Error(
          `Initial capital did not persist (sent ${sentInitial}, got ${savedInitial}). Try Save again.`
        );
      }

      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="backtest-settings-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="flex h-[min(640px,100%)] w-[min(560px,100%)] flex-col overflow-hidden rounded-[14px] border border-[var(--border)] bg-[var(--bg-card)] shadow-[var(--shadow-lg)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--divider)] px-4 py-3.5">
          <div>
            <h2 id="backtest-settings-title" className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">
              Backtest properties
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 gap-0.5 border-b border-[var(--border)] px-3" role="tablist">
            {TABS.map((label, i) => (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={tab === i}
                onClick={() => setTab(i)}
                className={`-mb-px cursor-pointer border-b-2 px-2.5 py-2 text-[13px] ${
                  tab === i
                    ? "border-[var(--primary)] text-[var(--text-primary)]"
                    : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-3 shrink-0 rounded-[10px] bg-[var(--danger-bg)] px-3 py-2 text-sm text-[var(--danger)]">
              {error}
            </div>
          )}

          {/* Fixed-height pane so Inputs / Properties / Extras share the same modal size */}
          <div className="drawer-fields min-h-0 flex-1 overflow-y-auto pb-4 [&_input:not([type=checkbox])]:!h-7 [&_input:not([type=checkbox])]:!min-h-0 [&_input:not([type=checkbox])]:!rounded-md [&_input:not([type=checkbox])]:!px-2 [&_input:not([type=checkbox])]:!py-0 [&_input:not([type=checkbox])]:!text-xs [&_input:not([type=checkbox])]:font-mono [&_select]:!h-7 [&_select]:!min-h-0 [&_select]:!w-[7.5rem] [&_select]:!rounded-md [&_select]:!py-0 [&_select]:!pl-2 [&_select]:!text-xs">
            {tab === 3 ? (
              <div className="px-4 py-3.5">
                <p className="mb-3 text-xs text-[var(--text-muted)]">
                  Latest plot values from the last run. They update when you run again.
                </p>
                <PlotValuesTable outputs={outputs} waiting={outputsWaiting} className="min-h-[240px]" />
              </div>
            ) : !ready ? (
              <div className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</div>
            ) : (
              <>
                <div className={tab === 0 ? undefined : "hidden"} aria-hidden={tab !== 0}>
                  <label className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 border-b border-[var(--divider)] px-4 py-3.5">
                    <span>
                      <span className="block text-[13px] text-[var(--text-primary)]">Backtest name</span>
                      <span className="mt-0.5 block text-[11px] text-[var(--text-muted)]">Shown in the backtest list</span>
                    </span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-44"
                      placeholder="Name"
                    />
                  </label>
                </div>
                <div className={tab === 1 ? undefined : "hidden"} aria-hidden={tab !== 1}>
                  {inputSpecs.length > 0 ? (
                    <DynamicForm
                      specs={inputSpecs}
                      values={strategyValues}
                      onChange={setStrategyConfig}
                      layout="rows"
                      density="compact"
                      columns={1}
                    />
                  ) : (
                    <div className="text-sm text-[var(--text-muted)]">
                      Strategy inputs appear here after the script is parsed (input.* declarations).
                    </div>
                  )}
                </div>
                <div className={tab === 0 ? undefined : "hidden"} aria-hidden={tab !== 0}>
                  <DynamicForm
                    specs={enginePropertySpecs}
                    values={enginePropertyValues}
                    onChange={(next) =>
                      applyEnginePatch(setEngineConfig, engineConfigRef, next, enginePropertySpecs)
                    }
                    layout="rows"
                    density="compact"
                    columns={1}
                  />
                  <div className="flex flex-col gap-2.5 px-4 py-3.5 text-[13px]">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5">
                      <label htmlFor="bt-bar-magnifier" className="min-w-0">
                        <span className="block text-[var(--text-primary)]">Using bar magnifier</span>
                        <span className="block text-[11px] text-[var(--text-muted)]">
                          {magnifierOptions.length
                            ? `Fill orders along smaller candles inside each ${backtest.timeframe} bar instead of an assumed path. More exact, slower.`
                            : `Not available on ${backtest.timeframe}: no shorter timeframe divides it.`}
                        </span>
                      </label>
                      <select
                        id="bt-bar-magnifier"
                        className="w-[7.5rem]"
                        value={barMagnifier}
                        disabled={magnifierOptions.length === 0}
                        onChange={(e) => setBarMagnifier(e.target.value)}
                      >
                        <option value="">Off</option>
                        {magnifierOptions.map((tf) => (
                          <option key={tf} value={tf}>
                            {tf}
                          </option>
                        ))}
                      </select>
                    </div>
                    {declaresMagnifier && !barMagnifier && magnifierOptions.length > 0 && (
                      <div className="text-xs text-[var(--warning)]">The script sets use_bar_magnifier; pick a timeframe to backtest with it.</div>
                    )}
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[var(--text-secondary)]">Recalculate</span>
                      <span className="text-[var(--text-muted)]" title="Read from the script's strategy() on every run (calc_on_order_fills, calc_on_every_tick).">
                        {recalculateLabel(strategyInfo ? declaredRecalculate(strategyInfo) : backtest.recalculate)}
                      </span>
                    </div>
                    {scriptEngine && (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="min-w-0 flex-1 text-xs text-[var(--text-muted)]">
                          {matchesScript
                            ? "These are the properties the script declares, as TradingView would run it."
                            : "Some properties differ from what the script declares."}
                        </span>
                        <button
                          type="button"
                          className="btn btn-soft btn-sm"
                          disabled={matchesScript}
                          title="Order size, commission, leverage, slippage and fills as strategy() declares them; Pine's defaults for the rest."
                          onClick={() => applyEnginePatch(setEngineConfig, engineConfigRef, scriptEngine, enginePropertySpecs)}
                        >
                          Use the script's properties
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className={tab === 2 ? undefined : "hidden"} aria-hidden={tab !== 2}>
                  {engineExtrasSpecs.length > 0 && (
                    <DynamicForm
                      specs={engineExtrasSpecs}
                      values={engineExtrasValues}
                      onChange={(next) =>
                        applyEnginePatch(setEngineConfig, engineConfigRef, next, engineExtrasSpecs)
                      }
                      layout="rows"
                      density="compact"
                      columns={1}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2.5 border-t border-[var(--divider)] px-4 py-4">
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !ready}
            onClick={() => void handleSave()}
          >
            {saving ? "Saving…" : "Apply & re-run"}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
